import { Injectable, OnDestroy } from '@angular/core';

import { Instant } from '@js-joda/core';

import { Clock } from './clock';
import { AbortError } from './abort-error';
import { Disposable } from './interfaces';

// The real clock; every timer is tracked so a torn-down runtime leaves none behind.
@Injectable()
export class SystemClockService extends Clock implements OnDestroy {
  private readonly timers = new Set<() => void>();
  private destroyed = false;

  public override get activeTimers(): number {
    return this.timers.size;
  }

  public override now(): Instant {
    return Instant.ofEpochMilli(Date.now());
  }


  public override after(milliseconds: number, signal?: AbortSignal): Promise<void> {
    this.assertValidDelay(milliseconds);

    if (signal?.aborted || this.destroyed) {
      return Promise.reject(new AbortError());
    }

    return new Promise<void>((resolve, reject) => {
      // Both exits go through `clean`, so a long-lived signal cannot accumulate abort listeners.
      const clean = (): void => {
        clearTimeout(timer);
        this.timers.delete(cancel);
        signal?.removeEventListener('abort', cancel);
      };
      const cancel = (): void => {
        clean();
        reject(new AbortError());
      };
      const timer = setTimeout(() => {
        clean();
        resolve();
      }, milliseconds);
      this.timers.add(cancel);
      signal?.addEventListener('abort', cancel, { once: true });
    });
  }

  public override every(milliseconds: number, jitterMilliseconds: number, callback: () => void): Disposable {
    this.assertValidDelay(milliseconds, true);
    this.assertValidDelay(jitterMilliseconds);

    if (this.destroyed) {
      return { dispose: (): void => undefined };
    }

    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const dispose = (): void => {
      disposed = true;
      clearTimeout(timer);
      this.timers.delete(dispose);
    };
    const schedule = (): void => {
      // Re-armed per tick, not setInterval: jitter keeps displays that booted together out of lockstep.
      const delay = Math.max(1, milliseconds + (Math.random() * 2 - 1) * jitterMilliseconds);
      timer = setTimeout(() => {
        try {
          callback();
        } finally {
          // `finally`, so one throwing tick does not silently kill the interval for good.
          if (!disposed) {
            schedule();
          }
        }
      }, delay);
    };
    this.timers.add(dispose);
    schedule();

    return { dispose };
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    [...this.timers].forEach(cancel => cancel());
  }
}
